/**
 * Copy and metadata for the /videos hub and each video watch page.
 * Shared by the page components and scripts/generate-static-pages.ts so the
 * rendered page, static meta, JSON-LD and video sitemap always agree.
 * Links inside copy use [anchor](url) syntax.
 */
export interface WatchPage {
  slug: string;
  videoId: string;
  title: string;
  description: string;
  h1: string;
  intro: string;
  about: string[];
  built: string[];
  schemaDescription: string;
  card: string;
}

export const VIDEOS_HUB = {
  title: "Project Videos North Jersey | Masonry Pro Construction",
  description:
    "Watch real Masonry Pro jobs in Wayne, Livingston, Kinnelon, Oakland and North Caldwell, NJ: patios, retaining walls, driveways, steps and stone veneer.",
  h1: "Project Videos From Across North Jersey",
  intro:
    "Every video here is a real job our crew built. You'll see patios, retaining walls, driveways, front steps and stone veneer in towns across Passaic, Bergen, Essex and Morris counties. Each page shows the finished work, tells you what was there before, and lists the materials we used. If you're planning a project of your own, it's a good way to see how we handle a sloped yard, an old concrete slab or a tired front entry.",
};

export const WATCH_PAGES: WatchPage[] = [
  {
    slug: "pool-paver-patio-outdoor-kitchen-wayne-nj",
    videoId: "M16CRt8dmJ4",
    title: "Pool Paver Patio and Outdoor Kitchen in Wayne, NJ | Masonry Pro",
    description:
      "Watch a Wayne, NJ backyard go from an old concrete pool deck to Cambridge pavers, an outdoor kitchen, a fire pit and new steps. Watch the project video.",
    h1: "Pool Paver Patio, Outdoor Kitchen and Fire Pit in Wayne, NJ",
    intro: "A full backyard rebuild around an existing pool in Wayne, New Jersey.",
    about: [
      "This homeowner first called us to redo his front steps. He liked how they came out, so he brought us back for the whole backyard.",
      "The yard had an old concrete patio around the pool that had seen better days. He wanted one big space where his family could cook, sit around a fire and host parties.",
      "We broke out the old concrete and laid Cambridge pavers in its place. Around the existing pool we set new Techo-Bloc coping. Then we built a custom outdoor kitchen and a fire pit. The yard slopes, so we added several sets of steps to tie the levels together and make the layout easy to walk. We finished the landscaping around it so the whole yard looks like one project.",
      "Start to finish, the job took about three weeks. If you have a dated concrete pool deck and you're thinking about [paver patio installation](/paving-services/paver-patios), this is a good look at what a full rebuild involves.",
    ],
    built: [
      "Old concrete patio removed and replaced with Cambridge pavers",
      "Techo-Bloc coping around the existing pool",
      "Custom outdoor kitchen and a fire pit",
      "Several sets of steps to handle the slope",
      "Landscaping around the finished space",
    ],
    schemaDescription:
      "Masonry Pro Construction replaced an old concrete patio in Wayne, NJ with a Cambridge paver pool patio, outdoor kitchen, fire pit and steps.",
    card: "An old concrete pool deck in Wayne became a [pool paver patio with an outdoor kitchen and fire pit](/videos/pool-paver-patio-outdoor-kitchen-wayne-nj).",
  },
  {
    slug: "paver-patio-livingston-nj",
    videoId: "Po36ZvNh7ro",
    title: "Large Paver Patio Installation in Livingston, NJ | Masonry Pro",
    description:
      "An old concrete patio in Livingston, NJ replaced with an oversized Cambridge XL paver patio with room for basketball and parties. Watch the project video.",
    h1: "Oversized Paver Patio Installation in Livingston, NJ",
    intro: "A concrete patio swapped for a much bigger paver patio in Livingston, New Jersey.",
    about: [
      "This Livingston homeowner had an old concrete patio that was too small for how his family actually uses the yard. He had two goals. He wanted enough room to host parties and barbecues, and he wanted a flat, open area where his son could play basketball.",
      "We took out the concrete and built a much larger patio with Cambridge XL Smooth pavers in Limestone Quarry Blend. The bigger pavers suit a big space and give it a clean, modern look.",
      "The backyard has a slope, so we didn't fight it. We added a step along the right side of the patio to handle the change in height and make a smooth move from one area to the next.",
      "Now the same patio works as a party space and a basketball court. The homeowner was very happy with how it turned out. It's a good example of how our [paver patios across North Jersey](/paving-services/paver-patios) are sized around the way a family lives, not just the footprint of the old slab.",
    ],
    built: [
      "Old concrete patio removed",
      "Oversized patio in Cambridge XL Smooth pavers, Limestone Quarry Blend",
      "A step on the right side to handle the slope",
      "Open, flat area big enough for basketball",
    ],
    schemaDescription:
      "Masonry Pro Construction replaced a concrete patio in Livingston, NJ with an oversized Cambridge XL paver patio big enough for basketball.",
    card: "A family in Livingston wanted room for parties and basketball, so we built an [oversized paver patio in Livingston](/videos/paver-patio-livingston-nj).",
  },
  {
    slug: "retaining-wall-wayne-nj",
    videoId: "RTdf5C02SHY",
    title: "Retaining Wall Installation in Wayne, NJ | Masonry Pro",
    description:
      "A rocky, unfinished slope in Wayne, NJ turned into a Cambridge Sigma block retaining wall with low-voltage lighting and new landscaping. Watch the video.",
    h1: "Retaining Wall Installation in Wayne, NJ",
    intro: "A new block retaining wall with built-in lighting for a family that had just moved in.",
    about: [
      "These homeowners had just moved into their house in Wayne. The backyard had a rocky slope that made the whole yard look unfinished, and it wasn't a place the kids could really play.",
      "They wanted a clean, usable backyard where the family could spend time together. A retaining wall was the way to get there.",
      "We built the wall with Cambridge Sigma wall blocks in Onyx Natural. The dark block gives the yard a sharp, structured look. We also ran low-voltage landscape lighting along the wall, so it looks just as good after dark. Then we finished the landscaping around it to tie everything together.",
      "The yard went from a rough slope to a finished space the family can use. If your yard has a slope that's going to waste, our [retaining wall installation in Totowa and North Jersey](/masonry-services/retaining-walls) page explains how we plan and build walls like this one.",
    ],
    built: [
      "Retaining wall in Cambridge Sigma wall blocks, Onyx Natural",
      "Low-voltage landscape lighting on the wall",
      "Landscaping around the finished wall",
    ],
    schemaDescription:
      "Masonry Pro Construction turned a rocky slope in Wayne, NJ into a Cambridge block retaining wall with low-voltage lighting.",
    card: "A rocky slope in Wayne turned into a [lit block retaining wall in Wayne](/videos/retaining-wall-wayne-nj).",
  },
  {
    slug: "retaining-walls-paver-apron-kinnelon-nj",
    videoId: "aJJQS6FX--0",
    title: "Retaining Walls and Paver Apron in Kinnelon, NJ | Masonry Pro",
    description:
      "Retaining walls with drainage, a paver driveway apron, masonry columns and lighting along a Kinnelon, NJ driveway. One wall job that grew. Watch the video.",
    h1: "Retaining Walls, Paver Driveway Apron and Columns in Kinnelon, NJ",
    intro: "What started as driveway walls became a new front entrance in Kinnelon, New Jersey.",
    about: [
      "We were first hired in Kinnelon to build retaining walls along both sides of a driveway. Behind the walls we installed drainage, so water has somewhere to go instead of building up against the block.",
      "The homeowner liked the walls enough to keep going. We added a paver driveway apron in Limestone Quarry Blend, which gives the entrance a clean, finished edge where it meets the road. We built masonry columns along the driveway and added low-voltage landscape lighting to show off the stonework at night. Decorative river rock and landscaping finished the look.",
      "Later, he called us back again to redo his front steps and walkway in pavers.",
      "One wall job turned into the whole front of the property. That kind of repeat work is how we know the first job was done right. For more on walls along driveways and slopes, see our [retaining walls for sloped North Jersey properties](/masonry-services/retaining-walls).",
    ],
    built: [
      "Retaining walls on both sides of the driveway, with drainage behind them",
      "Paver driveway apron in Limestone Quarry Blend",
      "Masonry columns along the driveway with low-voltage lighting",
      "Paver front steps and walkway (a later visit)",
      "Decorative river rock and landscaping",
    ],
    schemaDescription:
      "Masonry Pro Construction built retaining walls with drainage, a paver driveway apron, masonry columns and a paver walkway in Kinnelon, NJ.",
    card: "A wall job along a Kinnelon driveway grew into [retaining walls, a paver apron and columns in Kinnelon](/videos/retaining-walls-paver-apron-kinnelon-nj).",
  },
  {
    slug: "paver-driveway-oakland-nj",
    videoId: "50WpbxLOZa8",
    title: "Paver Driveway and Walkway in Oakland, NJ | Masonry Pro",
    description:
      "A Cambridge paver driveway, matching walkway and masonry mailbox with a bluestone cap in Oakland, NJ, built on proper grading and drainage. Watch the video.",
    h1: "Paver Driveway, Walkway and Masonry Mailbox in Oakland, NJ",
    intro: "A new front driveway and walkway for a returning customer in Oakland, New Jersey.",
    about: [
      "We built a retaining wall in this homeowner's backyard the year before. The next spring, he asked us back to redo the whole front of the house.",
      "We installed Cambridge pavers in Limestone Quarry Blend for both the driveway and the walkway, so the entrance looks like one design. To finish it off, we built a custom masonry mailbox with a natural bluestone cap.",
      "The part you can't see in the video matters most. A driveway carries cars every day and goes through freeze and thaw every winter. So we paid close attention to grading, slope and drainage under the pavers. Water needs to run away from the house and off the driveway, not sit under it. That's what keeps a paver driveway flat for the long run.",
      "He trusted us with his backyard, then his front yard. If you're weighing [paver driveway installation](/paving-services/paver-driveways), this is the kind of prep we do on every driveway.",
    ],
    built: [
      "Paver driveway in Cambridge pavers, Limestone Quarry Blend",
      "Matching paver walkway to the front door",
      "Custom masonry mailbox with a natural bluestone cap",
      "Grading, slope and drainage set under the pavers",
    ],
    schemaDescription:
      "Masonry Pro Construction installed a Cambridge paver driveway, matching walkway and a masonry mailbox with a bluestone cap in Oakland, NJ.",
    card: "A returning customer in Oakland got a [paver driveway, walkway and masonry mailbox](/videos/paver-driveway-oakland-nj).",
  },
  {
    slug: "outdoor-kitchen-fire-pit-north-caldwell-nj",
    videoId: "Fogsfyz7P18",
    title: "Outdoor Kitchen and Fire Pit, North Caldwell, NJ | Masonry Pro",
    description:
      "A North Caldwell, NJ backyard with no patio became an outdoor kitchen with granite counters, a fire pit and bar seating for guests. Watch the project video.",
    h1: "Patio, Outdoor Kitchen and Fire Pit in North Caldwell, NJ",
    intro: "A full outdoor living space built from an empty backyard in North Caldwell, New Jersey.",
    about: [
      "When this North Caldwell homeowner called us, the backyard had no patio at all. Nowhere to sit, cook or have people over. He wanted a place to relax, entertain friends and spend time with his family.",
      "We sat down with him and worked out the design together before anything was dug. The centerpiece is a custom outdoor kitchen with a built-in grill, a sink and a mini fridge, topped with granite counters. We also built a fire pit with its own seating area. And we added a bar-style counter with room for stools, so guests can hang out while the food is on the grill.",
      "The result is one backyard with three places to gather: the kitchen, the bar and the fire. It's set up for barbecues, family parties and regular weeknights outside.",
      "If you're planning something similar, our [outdoor kitchens](/masonry-services/outdoor-kitchens) page covers what goes into a build like this.",
    ],
    built: [
      "Custom backyard design, planned with the homeowner from the start",
      "Outdoor kitchen with built-in grill, sink, mini fridge and granite counters",
      "Fire pit with a dedicated seating area",
      "Bar-style seating with room for stools",
    ],
    schemaDescription:
      "Masonry Pro Construction built a patio, outdoor kitchen with granite counters, fire pit and bar seating in a North Caldwell, NJ backyard.",
    card: "An empty North Caldwell backyard became a [patio with an outdoor kitchen and fire pit](/videos/outdoor-kitchen-fire-pit-north-caldwell-nj).",
  },
  {
    slug: "paver-walkway-steps-north-caldwell-nj",
    videoId: "Z1lcOlPIbWI",
    title: "Paver Walkway and Steps in North Caldwell, NJ | Masonry Pro",
    description:
      "A North Caldwell, NJ front entry rebuilt with Cambridge Sigma block steps, built-in lighting and a Three-Piece paver walkway with a border. Watch the video.",
    h1: "Paver Walkway and Block Front Steps in North Caldwell, NJ",
    intro: "A new front walkway and steps that changed the look of a home in North Caldwell, New Jersey.",
    about: [
      "This North Caldwell homeowner wanted a front entrance that looked more modern and more welcoming, and that suited the house.",
      "We rebuilt the front steps with Cambridge Sigma Smooth wall blocks and finished them with bullnose caps for a clean, rounded edge. We built low-voltage lights right into the steps, so they're easy to see at night and the stonework stands out after dark.",
      "For the walkway, we used the Cambridge Three-Piece Paver System in Sandstone Light with a Mocha border. The darker border frames the lighter field and gives the walk a sharp outline from the street.",
      "The homeowners were very happy and left us a five-star review. The next year, they asked us back to build a backyard patio in the same paver colors, so the front and back match. If your front walk is due, our [paver walkway installation](/paving-services/paver-walkways) page explains how we build them to last.",
    ],
    built: [
      "Front steps in Cambridge Sigma Smooth wall blocks with bullnose caps",
      "Low-voltage lighting built into the steps",
      "Walkway in the Cambridge Three-Piece Paver System, Sandstone Light with a Mocha border",
    ],
    schemaDescription:
      "Masonry Pro Construction built a Cambridge paver walkway and block front steps with built-in lighting for a home in North Caldwell, NJ.",
    card: "A dated front entry in North Caldwell got a [new paver walkway and lit block steps](/videos/paver-walkway-steps-north-caldwell-nj).",
  },
  {
    slug: "stone-veneer-wayne-nj",
    videoId: "t2rG1WND0ug",
    title: "Stone Veneer Entry and Steps in Wayne, NJ | Masonry Pro",
    description:
      "Stone veneer around a new front door in Wayne, NJ, with matching veneer and bluestone steps on a reinforced concrete platform. Watch the project video.",
    h1: "Stone Veneer Entry and Bluestone Steps in Wayne, NJ",
    intro: "A front entrance rebuilt to match a new front door in Wayne, New Jersey.",
    about: [
      "This Wayne homeowner had just put in a new front door. The entrance around it didn't match anymore, and they wanted the front of the house to look more finished.",
      "We installed stone veneer around the entrance, so the door now sits in a stone surround. Then we built the front steps with matching stone veneer and thermally finished bluestone. The bluestone gives the steps a clean finish that pairs well with the stone.",
      "The most important part is under it all. We poured a new reinforced concrete platform with rebar for the steps to sit on. Stone and bluestone are only as solid as what's under them, and a weak base is how front steps crack and shift after a few New Jersey winters.",
      "The finished entry matches the new door and gives the house a more polished look from the street. Our [stone veneer installation](/masonry-services/stone-veneer) page has more on how we install veneer on homes like this one.",
    ],
    built: [
      "Stone veneer around the front entrance",
      "Front steps in matching stone veneer with thermally finished bluestone",
      "New reinforced concrete platform with rebar under the steps",
    ],
    schemaDescription:
      "Masonry Pro Construction added stone veneer, bluestone steps and a reinforced concrete platform to a front entry in Wayne, NJ.",
    card: "A new front door in Wayne got a matching [stone veneer entry with bluestone steps](/videos/stone-veneer-wayne-nj).",
  },
];

export function getWatchPage(slug: string): WatchPage | undefined {
  return WATCH_PAGES.find((p) => p.slug === slug);
}
